/**
 * JobApplicationPage — Sprint 2 / TASK 5-1
 *
 * Full-screen mobile-first application form for a specific GoGrabJob career
 * opening.  Uses a custom shell (not PageRampShell which requires auth) so
 * unauthenticated candidates can apply without signing in.
 *
 * URL: /careers/<slug>/apply
 *
 * Flow:
 *   1. Load career post by slug (published only).
 *   2. Show a single-page form: name, email, phone*, LinkedIn*, portfolio*,
 *      cover note*, resume (PDF, required).
 *   3. On submit: POST multipart/form-data to `submit-job-application` edge fn.
 *   4. Success state: "Application received!" with email confirmation note.
 *
 * Hard rules:
 *   • Name + email + resume are required; others are optional.
 *   • LinkedIn / portfolio are URL-validated client-side.
 *   • Resume must be ≤ 5 MB and PDF.
 *   • Apply CTA still works even when company name is masked (blind mode).
 *   • Form is fully accessible: labels, focus rings, Enter-to-submit.
 */

import {
  ArrowLeft,
  CheckCircle2,
  FileText,
  Globe,
  Linkedin,
  Loader2,
  Mail,
  Phone,
  Send,
  Upload,
  User,
  X,
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

import MarketingLandingNav, { MARKETING_NAV_TOP_OFFSET } from '../components/marketing/MarketingLandingNav';
import { supabase } from '../lib/supabase';
import { submitCareerApplication } from '../services/careerApplicationService';
import { SEO } from '../shared/components/SEO';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CareerPost {
  id: string;
  slug: string;
  title: string;
  department: string | null;
  location: string | null;
  job_type: string | null;
}

interface FormState {
  name: string;
  email: string;
  phone: string;
  linkedin_url: string;
  portfolio_url: string;
  cover_note: string;
  resume: File | null;
}

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isValidEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
}

function isValidUrl(s: string): boolean {
  if (!s.trim()) return true; // optional
  try {
    const u = new URL(s.trim());
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

function getSlugFromUrl(): string {
  // Match /careers/<slug>/apply
  const m = window.location.pathname.match(/^\/careers\/([^/]+)\/apply/);
  return m ? m[1] : '';
}

/** SPA navigation — must dispatch popstate so AppStateManager updates the view. */
function navigateToPath(path: string): void {
  if (window.location.pathname !== path) {
    window.history.pushState({}, '', path);
  }
  window.dispatchEvent(new PopStateEvent('popstate'));
}

interface ApplySuccessPayload {
  name: string;
  email: string;
  role: string;
  department: string | null;
  location: string | null;
}

function successStorageKey(slug: string): string {
  return `ggj_career_apply_success:${slug}`;
}

function readStoredSuccess(slug: string): ApplySuccessPayload | null {
  try {
    const raw = sessionStorage.getItem(successStorageKey(slug));
    return raw ? (JSON.parse(raw) as ApplySuccessPayload) : null;
  } catch {
    return null;
  }
}

function storeSuccess(slug: string, payload: ApplySuccessPayload): void {
  sessionStorage.setItem(successStorageKey(slug), JSON.stringify(payload));
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface FieldProps {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}

const Field: React.FC<FieldProps> = ({ id, label, required, error, children }) => (
  <div>
    <label
      htmlFor={id}
      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
    >
      {label}
      {required && (
        <span className="ml-1 text-red-500" aria-hidden="true">*</span>
      )}
    </label>
    {children}
    {error && (
      <p id={`${id}-error`} role="alert" className="mt-1.5 text-xs text-red-600 dark:text-red-400">
        {error}
      </p>
    )}
  </div>
);

const inputClass = (hasError?: boolean) =>
  [
    'careers-marketing-input',
    hasError ? 'border-red-400 dark:border-red-500' : '',
  ].join(' ');

// ─── Success screen ───────────────────────────────────────────────────────────

const SuccessScreen: React.FC<{
  name: string;
  email: string;
  role: string;
  department?: string | null;
  location?: string | null;
  onBack: () => void;
}> = ({ name, email, role, department, location, onBack }) => (
  <div className="py-6 sm:py-10">
    <div className="home-problem-section__mesh careers-listing-mesh overflow-visible">
      <div className="hiw-mesh-layer" aria-hidden />
      <div className="home-problem-section__mesh-inner px-6 py-8 sm:px-10 sm:py-10 text-center">
        <div className="relative mx-auto mb-6 w-fit">
          <div className="w-16 h-16 rounded-full bg-[#15803d]/10 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-9 h-9 text-[#15803d]" aria-hidden />
          </div>
        </div>

        <p className="home-problem-section__eyebrow">Application submitted</p>
        <h2 className="home-problem-section__title !mx-0 !max-w-none text-2xl sm:text-3xl mb-3">
          Thank you, <span className="home-problem-section__title-accent">{name.split(' ')[0] || name}</span>!
        </h2>
        <p className="home-problem-section__subtitle !mx-auto !max-w-lg text-sm sm:text-base mb-5">
          Your application for <strong className="text-stone-800 dark:text-stone-100">{role}</strong> is in.
          We sent a confirmation to <strong className="text-[#15803d]">{email}</strong>.
        </p>

        {(department || location) && (
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            {department && <span className="careers-dept-pill">{department}</span>}
            {location && <span className="text-xs text-stone-500 dark:text-stone-400">{location}</span>}
          </div>
        )}

        <div className="careers-apply-next-steps text-left max-w-md mx-auto mb-8 p-4 sm:p-5">
          <p className="text-xs font-bold uppercase tracking-wider text-stone-500 dark:text-stone-400 mb-3">
            What happens next
          </p>
          <ol className="space-y-2.5 text-sm text-stone-600 dark:text-stone-300 list-none m-0 p-0">
            {[
              'Our hiring team reviews your resume and profile.',
              'If you are shortlisted, we will email you within 5–7 business days.',
              'Keep an eye on your inbox (and spam folder) for updates.',
            ].map((step, i) => (
              <li key={step} className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#15803d]/15 text-[#15803d] text-xs font-bold flex items-center justify-center">
                  {i + 1}
                </span>
                <span className="pt-0.5">{step}</span>
              </li>
            ))}
          </ol>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-sm mx-auto">
          <button
            type="button"
            onClick={onBack}
            className="w-full py-3 rounded-xl border border-stone-200 dark:border-stone-600 text-sm font-medium text-stone-600 dark:text-stone-300 hover:border-[#15803d] hover:text-[#15803d] transition-colors"
          >
            View all openings
          </button>
          <a
            href="/"
            className="careers-marketing-btn-primary w-full py-3"
          >
            Explore GoGrabJob
          </a>
        </div>
      </div>
    </div>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

export interface JobApplicationPageProps {
  slug?: string;
  onNavigate?: (view: string) => void;
}

const JobApplicationPage: React.FC<JobApplicationPageProps> = ({ slug: propSlug, onNavigate }) => {
  const slug = propSlug || getSlugFromUrl();

  // ── Data ─────────────────────────────────────────────────────────────────
  const [post, setPost] = useState<CareerPost | null>(null);
  const [loadingPost, setLoadingPost] = useState(true);
  const [postError, setPostError] = useState<string | null>(null);

  // ── Form ─────────────────────────────────────────────────────────────────
  const [form, setForm] = useState<FormState>({
    name: '',
    email: '',
    phone: '',
    linkedin_url: '',
    portfolio_url: '',
    cover_note: '',
    resume: null,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [successPayload, setSuccessPayload] = useState<ApplySuccessPayload | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const resumeInputRef = useRef<HTMLInputElement>(null);

  // Restore success screen after refresh / SPA remount (?submitted=1)
  useEffect(() => {
    if (!slug) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('submitted') !== '1') return;
    const stored = readStoredSuccess(slug);
    if (stored) {
      setSuccessPayload(stored);
      setSubmitStatus('success');
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [slug]);

  // ── Load career post ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!slug) {
      setPostError('Job opening not found.');
      setLoadingPost(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('career_posts')
        .select('id, slug, title, department, location, job_type')
        .eq('slug', slug)
        .eq('status', 'published')
        .maybeSingle<CareerPost>();

      if (cancelled) return;
      if (error || !data) setPostError('This job opening is no longer available.');
      else setPost(data);
      setLoadingPost(false);
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const set = (field: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
      if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
    };

  const handleResumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (file && file.type !== 'application/pdf') {
      setErrors((prev) => ({ ...prev, resume: 'Only PDF files are accepted.' }));
      return;
    }
    if (file && file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, resume: 'Resume must be under 5 MB.' }));
      return;
    }
    setForm((prev) => ({ ...prev, resume: file }));
    setErrors((prev) => ({ ...prev, resume: undefined }));
  };

  const validate = (): boolean => {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.name.trim()) next.name = 'Full name is required.';
    if (!form.email.trim() || !isValidEmail(form.email)) next.email = 'A valid email address is required.';
    if (form.linkedin_url && !isValidUrl(form.linkedin_url))
      next.linkedin_url = 'Enter a valid URL (https://…).';
    if (form.portfolio_url && !isValidUrl(form.portfolio_url))
      next.portfolio_url = 'Enter a valid URL (https://…).';
    if (!form.resume) next.resume = 'Please attach your resume (PDF).';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (submitStatus === 'submitting' || !post) return;
    if (!validate()) return;

    setSubmitStatus('submitting');
    setServerError(null);

    try {
      const fd = new FormData();
      fd.append('career_post_id', post.id);
      fd.append('name', form.name.trim());
      fd.append('email', form.email.trim());
      if (form.phone.trim()) fd.append('phone', form.phone.trim());
      if (form.linkedin_url.trim()) fd.append('linkedin_url', form.linkedin_url.trim());
      if (form.portfolio_url.trim()) fd.append('portfolio_url', form.portfolio_url.trim());
      if (form.cover_note.trim()) fd.append('cover_note', form.cover_note.trim());
      fd.append('resume', form.resume!);

      await submitCareerApplication(fd);

      const payload: ApplySuccessPayload = {
        name: form.name.trim(),
        email: form.email.trim(),
        role: post.title,
        department: post.department,
        location: post.location,
      };
      storeSuccess(post.slug, payload);
      setSuccessPayload(payload);
      setSubmitStatus('success');

      const successUrl = `${window.location.pathname}?submitted=1`;
      window.history.replaceState({}, '', successUrl);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setServerError(msg);
      setSubmitStatus('error');
    }
  };

  const handleBack = () => {
    if (slug) navigateToPath(`/careers/${slug}`);
    else navigateToPath('/careers');
  };

  const handleViewAllOpenings = () => {
    navigateToPath('/careers');
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const isSubmitting = submitStatus === 'submitting';
  const showSuccess = submitStatus === 'success' && successPayload;

  return (
    <div className="careers-marketing-page min-h-screen flex flex-col flex-1 w-full transition-colors">
      {post && (
        <SEO
          title={`Apply — ${post.title} | GoGrabJob Careers`}
          description={`Apply for ${post.title} at GoGrabJob. We're hiring!`}
          canonical={`https://www.gograbjob.com/careers/${post.slug}/apply`}
        />
      )}

      <MarketingLandingNav onNavigate={onNavigate} activeView="careers" variant="marketing" />

      {/* ── Content ── */}
      <main className={`flex-1 max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12 w-full ${MARKETING_NAV_TOP_OFFSET}`}>

        {/* Loading / error states */}
        {loadingPost ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="careers-apply-spinner animate-spin" aria-hidden />
          </div>
        ) : postError ? (
          <div className="text-center py-20">
            <p className="text-gray-500 dark:text-gray-400">{postError}</p>
            <button
              type="button"
              onClick={handleBack}
              className="mt-4 text-sm careers-apply-link"
            >
              View all openings
            </button>
          </div>
        ) : showSuccess ? (
          <SuccessScreen
            name={successPayload.name}
            email={successPayload.email}
            role={successPayload.role}
            department={successPayload.department}
            location={successPayload.location}
            onBack={handleViewAllOpenings}
          />
        ) : post ? (
          <>
            <button
              type="button"
              onClick={handleBack}
              className="flex items-center gap-1.5 text-sm text-stone-600 dark:text-stone-400 hover:text-[#15803d] mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden />
              Back to job posting
            </button>

            {/* Header */}
            <div className="mb-8 home-problem-section__mesh careers-listing-mesh">
              <div className="hiw-mesh-layer" aria-hidden />
              <div className="home-problem-section__mesh-inner px-5 py-6 sm:px-8">
              <p className="home-problem-section__eyebrow">GoGrabJob Careers</p>
              <div className="flex flex-wrap items-center gap-2 text-xs mb-3">
                {post.department && <span className="careers-dept-pill">{post.department}</span>}
                {post.location && <span className="text-stone-600 dark:text-stone-400">{post.location}</span>}
                {post.job_type && <span className="text-stone-500 dark:text-stone-400">· {post.job_type}</span>}
              </div>
              <h1 className="home-problem-section__title text-left !mx-0 !max-w-none text-2xl sm:text-3xl">
                Apply for <span className="home-problem-section__title-accent">{post.title}</span>
              </h1>
              <p className="home-problem-section__subtitle text-left !mx-0 !max-w-none mt-3 text-sm">
                Submit your resume below — no account needed. Fields marked <span className="text-red-500" aria-hidden>*</span> are required.
              </p>
              </div>
            </div>

            {/* Form */}
            <form
              onSubmit={handleSubmit}
              noValidate
              aria-label={`Application form for ${post.title}`}
              className="careers-marketing-card careers-apply-form p-5 sm:p-8"
            >
              <div className="space-y-5">

                {/* Name */}
                <Field id="name" label="Full name" required error={errors.name}>
                  <div className="relative">
                    <User className="careers-apply-field-icon absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" aria-hidden />
                    <input
                      id="name"
                      type="text"
                      autoComplete="name"
                      value={form.name}
                      onChange={set('name')}
                      aria-required="true"
                      aria-invalid={!!errors.name}
                      aria-describedby={errors.name ? 'name-error' : undefined}
                      placeholder="Jane Smith"
                      className={`${inputClass(!!errors.name)} pl-10`}
                    />
                  </div>
                </Field>

                {/* Email */}
                <Field id="email" label="Email address" required error={errors.email}>
                  <div className="relative">
                    <Mail className="careers-apply-field-icon absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" aria-hidden />
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={set('email')}
                      aria-required="true"
                      aria-invalid={!!errors.email}
                      aria-describedby={errors.email ? 'email-error' : undefined}
                      placeholder="jane@example.com"
                      className={`${inputClass(!!errors.email)} pl-10`}
                    />
                  </div>
                </Field>

                {/* Phone */}
                <Field id="phone" label="Phone number" error={errors.phone}>
                  <div className="relative">
                    <Phone className="careers-apply-field-icon absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" aria-hidden />
                    <input
                      id="phone"
                      type="tel"
                      autoComplete="tel"
                      value={form.phone}
                      onChange={set('phone')}
                      placeholder="+91 98765 43210"
                      className={`${inputClass(!!errors.phone)} pl-10`}
                    />
                  </div>
                </Field>

                {/* LinkedIn */}
                <Field id="linkedin_url" label="LinkedIn profile" error={errors.linkedin_url}>
                  <div className="relative">
                    <Linkedin className="careers-apply-field-icon absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" aria-hidden />
                    <input
                      id="linkedin_url"
                      type="url"
                      value={form.linkedin_url}
                      onChange={set('linkedin_url')}
                      aria-invalid={!!errors.linkedin_url}
                      aria-describedby={errors.linkedin_url ? 'linkedin_url-error' : undefined}
                      placeholder="https://linkedin.com/in/yourprofile"
                      className={`${inputClass(!!errors.linkedin_url)} pl-10`}
                    />
                  </div>
                </Field>

                {/* Portfolio */}
                <Field id="portfolio_url" label="Portfolio / Website" error={errors.portfolio_url}>
                  <div className="relative">
                    <Globe className="careers-apply-field-icon absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" aria-hidden />
                    <input
                      id="portfolio_url"
                      type="url"
                      value={form.portfolio_url}
                      onChange={set('portfolio_url')}
                      aria-invalid={!!errors.portfolio_url}
                      aria-describedby={errors.portfolio_url ? 'portfolio_url-error' : undefined}
                      placeholder="https://yourportfolio.com"
                      className={`${inputClass(!!errors.portfolio_url)} pl-10`}
                    />
                  </div>
                </Field>

                {/* Cover note */}
                <Field id="cover_note" label="Cover note">
                  <textarea
                    id="cover_note"
                    rows={5}
                    value={form.cover_note}
                    onChange={set('cover_note')}
                    placeholder="Tell us why you're excited about this role and what makes you a great fit…"
                    className={`${inputClass()} resize-y`}
                    maxLength={3000}
                  />
                  <p className="careers-apply-char-count mt-1 text-xs text-right">
                    {form.cover_note.length}/3000
                  </p>
                </Field>

                {/* Resume upload */}
                <Field id="resume" label="Resume (PDF)" required error={errors.resume}>
                  <div
                    onClick={() => resumeInputRef.current?.click()}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') resumeInputRef.current?.click(); }}
                    role="button"
                    tabIndex={0}
                    aria-label="Upload your resume PDF"
                    aria-describedby={errors.resume ? 'resume-error' : undefined}
                    className={[
                      'careers-apply-resume-zone',
                      errors.resume
                        ? 'careers-apply-resume-zone--error'
                        : form.resume
                        ? 'careers-apply-resume-zone--filled'
                        : '',
                    ].join(' ')}
                  >
                    {form.resume ? (
                      <>
                        <FileText className="w-8 h-8 text-green-600 dark:text-green-400 shrink-0" aria-hidden />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{form.resume.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {(form.resume.size / 1024).toFixed(0)} KB · PDF
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setForm((prev) => ({ ...prev, resume: null }));
                            if (resumeInputRef.current) resumeInputRef.current.value = '';
                          }}
                          aria-label="Remove resume"
                          className="shrink-0 p-1 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        >
                          <X className="w-4 h-4" aria-hidden />
                        </button>
                      </>
                    ) : (
                      <>
                        <Upload className="careers-apply-field-icon w-8 h-8 shrink-0" aria-hidden />
                        <div>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Click to upload your resume
                          </p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">PDF · Max 5 MB</p>
                        </div>
                      </>
                    )}
                  </div>
                  <input
                    ref={resumeInputRef}
                    id="resume-file"
                    type="file"
                    accept="application/pdf"
                    onChange={handleResumeChange}
                    className="sr-only"
                    tabIndex={-1}
                  />
                </Field>

                {/* Server error */}
                {submitStatus === 'error' && serverError && (
                  <div
                    role="alert"
                    className="flex items-start gap-3 px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800"
                  >
                    <X className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 shrink-0" aria-hidden />
                    <p className="text-sm text-red-700 dark:text-red-300">{serverError}</p>
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`careers-marketing-btn-primary w-full ${isSubmitting ? 'cursor-wait' : ''}`}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                      Submitting application…
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" aria-hidden />
                      Submit Application
                    </>
                  )}
                </button>

                <p className="careers-apply-footer-note">
                  By submitting you agree to our{' '}
                  <a href="/privacy-policy" className="careers-apply-link underline">
                    Privacy Policy
                  </a>
                  . Resume files are stored securely and shared only with the GoGrabJob hiring team.
                </p>

              </div>
            </form>
          </>
        ) : null}

      </main>
    </div>
  );
};

export default JobApplicationPage;
